import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Search, MapPin, Loader2, X } from 'lucide-react';
import { GeolocationService } from '@/services/geolocation';
import type { Location } from '@/types';

interface LocationSearchProps {
  onLocationSelect: (location: Location, query: string) => void;
  onUseCurrentLocation: () => void;
  isLoadingCurrentLocation?: boolean;
  placeholder?: string;
  className?: string;
}

export const LocationSearch: React.FC<LocationSearchProps> = ({
  onLocationSelect,
  onUseCurrentLocation,
  isLoadingCurrentLocation = false,
  placeholder = "Enter postcode, city, or area...",
  className = "",
}) => {
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Array<{location: Location, display: string}>>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const searchRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const clearSearch = useCallback(() => {
    setQuery('');
    setError(null);
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
  }, []);

  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsSearching(true);
    setError(null);

    try {
      const location = await GeolocationService.getLocationFromPostcode(searchQuery);

      if (location) {
        const suggestion = { location, display: searchQuery.toUpperCase() };
        setSuggestions([suggestion]);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
        setError('Location not found. Try a UK postcode or city name.');
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Search failed. Please try again.');
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (query.trim()) {
      searchTimeoutRef.current = setTimeout(() => performSearch(query), 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setError(null);
    }

    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [query, performSearch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter' && query.trim()) performSearch(query);
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < suggestions.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : suggestions.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          onLocationSelect(suggestions[selectedIndex].location, suggestions[selectedIndex].display);
          clearSearch();
        } else if (suggestions.length === 1) {
          onLocationSelect(suggestions[0].location, suggestions[0].display);
          clearSearch();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        searchRef.current?.blur();
        break;
    }
  };

  const handleSuggestionClick = (suggestion: {location: Location, display: string}) => {
    onLocationSelect(suggestion.location, suggestion.display);
    clearSearch();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        !searchRef.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`}>
      <div className="flex gap-2">
        {/* Search Input */}
        <div className="flex-1 relative">
          <input
            ref={searchRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            placeholder={placeholder}
            className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-400 bg-white text-gray-900 placeholder-gray-400 transition-shadow"
          />

          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />

          <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
            {isSearching ? (
              <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
            ) : query ? (
              <button onClick={clearSearch} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Clear search">
                <X className="w-4 h-4" />
              </button>
            ) : null}
          </div>

          {/* Error */}
          {error && (
            <div className="absolute top-full left-0 right-0 mt-1 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600 z-10">
              {error}
            </div>
          )}

          {/* Suggestions */}
          {showSuggestions && suggestions.length > 0 && (
            <div
              ref={suggestionsRef}
              className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 max-h-60 overflow-y-auto"
            >
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors ${
                    index === selectedIndex ? 'bg-amber-50 text-amber-800' : 'text-gray-700'
                  }`}
                >
                  <MapPin className="w-4 h-4 flex-shrink-0 text-gray-400" />
                  <span>{suggestion.display}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Use My Location */}
        <button
          onClick={onUseCurrentLocation}
          disabled={isLoadingCurrentLocation}
          className="flex items-center gap-1.5 px-3 py-2 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white text-sm font-medium rounded-lg transition-colors shadow-sm whitespace-nowrap"
          title="Use my current location"
        >
          {isLoadingCurrentLocation ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <MapPin className="w-4 h-4" />
          )}
          <span className="hidden sm:inline">Use My Location</span>
        </button>
      </div>
    </div>
  );
};

export default LocationSearch;
